import React, { useState, useRef, useCallback } from 'react';
import { useSources } from 'hooks';
import { store } from 'store/createStore';
import _ from 'lodash';
import { CSVLink } from 'react-csv';
import moment from 'moment';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import { IconButton } from '@material-ui/core';
import { MdDownload } from 'react-icons/md';
import { Paper } from '@material-ui/core';

import 'react-datepicker/dist/react-datepicker.css';

import SharedHeaderAnalytics from '../../Analytics/components/SharedHeaderAnalytics';
import { Table, Tooltip } from 'components';

import { formatDate } from 'utils/time';
import API from 'utils/api';

import styles from './CampaignHome.module.scss';

const CampaignHome = () => {
  const [dateFrom, setDateFrom] = useState(moment().subtract(7, 'd').toDate());
  const [dateTo, setDateTo] = useState(moment().toDate());
  const [campaignFilter, setCampaignFilter] = useState('all');
  const [pageViewCards, setPageViewCards] = useState([]);
  const [eventCards, setEventCards] = useState([]);
  const [dataReport, setDataReport] = useState([]);

  const [csvLink, setCsvLink] = useState();

  const tableRef = useRef();
  const sources = useSources();

  const getCampaignData = useCallback(
    async (filter, pageSize, page) => {
      const { currentGroup } = store.getState().auth;

      if (campaignFilter === 'points') {
        filter = {
          ...filter,
          $and: [{ points: { $exists: true } }, { points: { $ne: null } }],
        };
      } else if (campaignFilter === 'day1') {
        filter = {
          ...filter,
          $or: [
            { 'selectedTeams.1': { $exists: true } },
            { 'selectedTeams.2': { $exists: true } },
            { 'selectedTeams.3': { $exists: true } },
          ],
        };
      } else if (campaignFilter === 'day2') {
        filter = {
          ...filter,
          $or: [
            { 'selectedTeams.4': { $exists: true } },
            { 'selectedTeams.5': { $exists: true } },
            { 'selectedTeams.6': { $exists: true } },
          ],
        };
      }

      filter = JSON.stringify(filter);

      return API.getRugbyBclcAnalytics(
        {
          query: filter,
          group: currentGroup._id,
          pageSize,
          page,
          dateFrom: dateFrom,
          dateTo: dateTo,
          sort: 'createdAt',
        },
        sources
      );
    },
    [campaignFilter, dateFrom, dateTo]
  );

  const exportCSV = () => {
    let query = {};

    const filter = _.get(tableRef, 'current.state.filter', null);

    if (filter) {
      for (const keyFilter in filter) {
        query[keyFilter] = filter[keyFilter];
      }
    }

    getCampaignData(filter).then(({ data }) => {
      if (data && data.docs) {
        const users = data.docs.map(user => {
          let name = 'Anonymous user';

          if (user.name) {
            name = user.name;
          }

          return {
            User: name,
            Email: user.email,
            Phone: user.phone,
            Points: user.points,
            Subscribed: user.createdAt,
            'Game 1':
              user.selectedTeams && user.selectedTeams[1]
                ? user.selectedTeams[1].teamName
                : '',
            'Game 2':
              user.selectedTeams && user.selectedTeams[2]
                ? user.selectedTeams[2].teamName
                : '',
            'Game 3':
              user.selectedTeams && user.selectedTeams[3]
                ? user.selectedTeams[3].teamName
                : '',
            'Game 4':
              user.selectedTeams && user.selectedTeams[4]
                ? user.selectedTeams[4].teamName
                : '',
            'Game 5':
              user.selectedTeams && user.selectedTeams[5]
                ? user.selectedTeams[5].teamName
                : '',
            'Game 6':
              user.selectedTeams && user.selectedTeams[6]
                ? user.selectedTeams[6].teamName
                : '',
          };
        });
        setDataReport(users);
        csvLink.link.click();
      }
    });
  };

  const fetchData = useCallback(
    ({ pageSize, page, filter }) => {
      let query = {};

      if (filter) {
        for (const keyFilter in filter) {
          query[keyFilter] = filter[keyFilter];
        }
      }

      return getCampaignData(filter, pageSize, page);
    },
    [getCampaignData]
  );

  const getPageViews = () => {
    const dateRanges = [
      {
        startDate: moment(dateFrom).format('YYYY-MM-DD'),
        endDate: moment(dateTo).format('YYYY-MM-DD'),
      },
    ];

    const dimensions = [
      {
        name: 'pagePath',
      },
    ];

    const metrics = [
      {
        name: 'screenPageViews',
      },
    ];

    const orderBys = [
      {
        metric: { metricName: 'screenPageViews', orderType: 'NUMBER' },
        desc: true,
      },
    ];

    API.getGoogleReport({
      project: 'BCLC',
      filter: {
        dateRanges,
        dimensions,
        metrics,
        orderBys,
      },
    }).then(data => {
      let rows = _.get(data, 'reports.0.rows', []);

      rows = rows.map(row => {
        const data = _.get(row, 'metricValues.0.value', null);
        const title = _.get(row, 'dimensionValues.0.value', null);

        return { data, title };
      });

      setPageViewCards(rows);
    });
  };

  const getEventClick = () => {
    const dateRanges = [
      {
        startDate: moment(dateFrom).format('YYYY-MM-DD'),
        endDate: moment(dateTo).format('YYYY-MM-DD'),
      },
    ];

    const dimensions = [
      {
        name: 'eventName',
      },
    ];

    const metrics = [{ name: 'eventCount' }];

    const orderBys = [
      {
        metric: { metricName: 'eventCount', orderType: 'NUMBER' },
        desc: true,
      },
    ];

    API.getGoogleReport({
      project: 'BCLC',
      filter: {
        dateRanges,
        dimensions,
        metrics,
        orderBys,
      },
    }).then(data => {
      let rows = _.get(data, 'reports.0.rows', []);
      let events = [];
      for (const row of rows) {
        const data = _.get(row, 'metricValues.0.value', null);
        const title = _.get(row, 'dimensionValues.0.value', null);
        if (
          ![
            'scroll',
            'page_view',
            'user_engagement',
            'session_start',
            'first_visit',
          ].includes(title)
        ) {
          events.push({ data, title });
        }
      }

      setEventCards(events);
    });
  };

  const getAnalyticsData = () => {
    if (tableRef && tableRef.current) {
      tableRef.current.reload();
    }
    getPageViews();
    getEventClick();
  };

  const renderControlPanel = () => {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <IconButton onClick={exportCSV}>
          <Tooltip title="Click to export table data" placement="top">
            <MdDownload />
          </Tooltip>
        </IconButton>
      </div>
    );
  };

  const columns = [
    { title: 'Name', field: 'name' },
    { title: 'Email', field: 'email' },
    { title: 'Phone', field: 'phone' },
    { title: 'Points', field: 'points' },
    { title: 'Game 1', field: 'selectedTeams.1.teamName' },
    { title: 'Game 2', field: 'selectedTeams.2.teamName' },
    { title: 'Game 3', field: 'selectedTeams.3.teamName' },
    { title: 'Game 4', field: 'selectedTeams.4.teamName' },
    { title: 'Game 5', field: 'selectedTeams.5.teamName' },
    { title: 'Game 6', field: 'selectedTeams.6.teamName' },
    { title: 'Age', field: 'age' },
    {
      title: 'Subscribed',
      field: 'createdAt',
      filtering: false,
      render: rowData => <div>{formatDate(rowData.createdAt)}</div>,
      type: 'date',
    },
  ];

  return (
    <div className={styles.content}>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <div>
            <Typography className={styles.title} variant="h6" gutterBottom>
              Analytics
            </Typography>
          </div>
          <SharedHeaderAnalytics
            title={'Date range'}
            refreshData={() => getAnalyticsData()}
            setDateTo={date => setDateTo(date)}
            setDateFrom={date => setDateFrom(date)}
            dateTo={dateTo}
            dateFrom={dateFrom}
            campaignFilter={campaignFilter}
            setCampaignFilter={setCampaignFilter}
          />

          <div className={styles.tableContainer}>
            <CSVLink
              data={dataReport}
              filename="Users.csv"
              className="hidden"
              ref={r => setCsvLink(r)}
              target="_blank"
            />

            <Table
              title="Users2"
              columns={columns}
              data={fetchData}
              tableRef={tableRef}
              controlPanel={renderControlPanel()}
              search={false}
              filtering
            />
          </div>
        </Grid>
        <Grid item xs={5}>
          <Paper className={styles.paperCards}>
            <Typography variant="h5">Page views</Typography>
            {pageViewCards &&
              pageViewCards.map(card => (
                <Card elevation={1} key={card.title} className={styles.cards}>
                  <CardContent>
                    <Typography variant="h5">{card.data}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {card.title}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
          </Paper>
        </Grid>
        <Grid item xs={5}>
          <Paper className={styles.paperCards}>
            <Typography variant="h5">Events</Typography>
            {eventCards &&
              eventCards.map(card => (
                <Card elevation={1} key={card.title} className={styles.cards}>
                  <CardContent>
                    <Typography variant="h5">{card.data}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {card.title}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

export default CampaignHome;
